import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ColDef } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { ConsolidatedBoardRow } from './consolidatedBoards';
import { formatCurrency, generateMonthColumns } from './helpers';

interface ConsolidatedScenarioGridProps {
    // i18n key of the card title (budget namespace).
    titleKey: string;
    rows: ConsolidatedBoardRow[];
    months: string[];
    isLoading: boolean;
    // Shown instead of rows when the board has nothing to say (an adjustment table with no selection).
    emptyHintKey?: string;
}

const deltaColor = (value: unknown): string | undefined => {
    const num = Number(value);
    if (num > 0) return '#2e7d32';
    if (num < 0) return '#d32f2f';
    return undefined;
};

// One table of the board's consolidated layout: pinned label column, the version's month columns, and
// a pinned-right Summe. Read-only; row styling mirrors BudgetSummary (bold labels, indented sub-rows,
// delta colours) so the numbers read the same as on the Budget overview.
const ConsolidatedScenarioGrid = ({
    titleKey,
    rows,
    months,
    isLoading,
    emptyHintKey,
}: ConsolidatedScenarioGridProps) => {
    const { t } = useTranslation('budget');

    // ag-grid wants one flat object per row: the month keys become fields, so they are spread in.
    const rowData = useMemo(
        () =>
            rows.map((row) => ({
                ...row.values,
                label: row.label,
                rowKey: row.rowKey,
                __sum: row.sum,
                isSub: !!row.isSub,
                isDelta: !!row.isDelta,
                isScenario: !!row.isScenario,
                isForeign: !!row.isForeign,
            })),
        [rows]
    );

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(months).map((col) => ({
                ...col,
                cellStyle: (params: any): Record<string, string> => {
                    // generateMonthColumns' default is { textAlign: 'right' } — carry it through.
                    const style: Record<string, string> = { textAlign: 'right' };
                    if (params.data?.isDelta) {
                        const color = deltaColor(params.value);
                        if (color) style.color = color;
                    }
                    return style;
                },
            })),
        [months]
    );

    const sumColumn: ColDef = useMemo(
        () => ({
            headerName: t('common.sum'),
            field: '__sum',
            minWidth: 130,
            editable: false,
            pinned: 'right',
            type: 'numericColumn',
            cellStyle: (params: any): Record<string, string> => {
                const base: Record<string, string> = { fontWeight: 'bold', textAlign: 'right' };
                if (params.data?.isDelta) {
                    const color = deltaColor(params.value);
                    if (color) base.color = color;
                }
                return base;
            },
            valueFormatter: (params) => formatCurrency(params.value),
        }),
        [t]
    );

    const columnDefs = useMemo<ColDef[]>(
        () => [
            {
                headerName: '',
                field: 'label',
                // Wider than the board labels need: the scenario rows carry name + descriptors.
                minWidth: 280,
                pinned: 'left',
                editable: false,
                cellClass: 'notranslate',
                cellStyle: (params: any): Record<string, string> => {
                    if (params.data?.isScenario) {
                        const style: Record<string, string> = {
                            fontWeight: 'normal',
                            paddingLeft: '24px',
                            fontStyle: 'italic',
                        };
                        // A scenario from another budget version is a legitimate contributor, but not
                        // part of THIS version's own breakdown — muted so the distinction survives a
                        // clipped label.
                        if (params.data?.isForeign) style.color = '#757575';
                        return style;
                    }
                    return params.data?.isSub ? { fontWeight: 'normal', paddingLeft: '24px' } : { fontWeight: 'bold' };
                },
            },
            ...monthColumns,
            sumColumn,
        ],
        [monthColumns, sumColumn]
    );

    return (
        <Box className="budget-grid">
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
                <span>{t(titleKey)}</span>
            </Typography>
            {emptyHintKey && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    <span>{t(emptyHintKey)}</span>
                </Typography>
            )}
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                rowData={rowData}
                columnDefs={columnDefs}
                loading={isLoading}
                // editable:false at the DEFAULT level, not only per column: the wrapper's default is
                // `editable: true`, and this grid has no onCellValueChanged — a column added here without
                // its own flag would take typed input and throw it away.
                defaultColDef={{ editable: false, resizable: true, sortable: false, filter: false, suppressMenu: true }}
                getRowId={(params) => String(params.data.rowKey)}
                cellSelection
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('consolidated.empty')}</span>}
            />
        </Box>
    );
};

export default ConsolidatedScenarioGrid;
